import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import TemplateDesigner from "@/components/idc/TemplateDesigner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, LayoutTemplate, Pencil, Settings2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designing, setDesigning] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const editFileRef = useRef(null);
  const [form, setForm] = useState({ name: "", image: "", width: 3.5, height: 2.2, unit: "inch", orientation: "landscape" });
  const fileRef = useRef(null);

  const load = () => api.get("/idc/templates").then((r) => setTemplates(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Template cannot be larger than 5 MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveTemplate = async () => {
    if (!form.image) return toast.error("Upload a template image first");
    try {
      await api.post("/idc/templates", { ...form, layout: [] });
      toast.success("Template saved");
      setAdding(false);
      setForm({ name: "", image: "", width: 3.5, height: 2.2, unit: "inch", orientation: "landscape" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await api.delete(`/idc/templates/${id}`);
    load();
    toast.success("Deleted");
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({ name: t.name, width: t.width, height: t.height, unit: t.unit, orientation: t.orientation, image: "" });
  };

  const onEditFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Template cannot be larger than 5 MB");
    const reader = new FileReader();
    reader.onload = () => setEditForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/idc/templates/${editing.id}`, {
        name: editForm.name, width: editForm.width, height: editForm.height,
        unit: editForm.unit, orientation: editForm.orientation,
        layout: editing.layout || [], dataset_id: editing.dataset_id || "", image: editForm.image,
      });
      toast.success("Template updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  if (designing) {
    return <TemplateDesigner template={designing} onClose={() => { setDesigning(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold">Templates</h3>
        {!adding && (
          <Button onClick={() => setAdding(true)} data-testid="template-add-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus className="h-4 w-4" /> Add Template
          </Button>
        )}
      </div>

      {adding && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="template-add-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. School ID Card" data-testid="template-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Orientation</Label>
              <Select value={form.orientation} onValueChange={(v) => setForm({ ...form, orientation: v })}>
                <SelectTrigger data-testid="template-orientation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="landscape">Landscape</SelectItem>
                  <SelectItem value="portrait">Portrait</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Width</Label>
              <Input type="number" step="any" min="0.1" value={form.width} onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) || 0 })} data-testid="template-width-input" />
            </div>
            <div className="space-y-2">
              <Label>Height</Label>
              <Input type="number" step="any" min="0.1" value={form.height} onChange={(e) => setForm({ ...form, height: parseFloat(e.target.value) || 0 })} data-testid="template-height-input" />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="template-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inch">inch</SelectItem>
                  <SelectItem value="cm">cm</SelectItem>
                  <SelectItem value="mm">mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Template Image (max 5 MB)</Label>
            <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={(e) => onFile(e.target.files[0])} />
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="template-image-upload-btn"><Upload className="h-4 w-4" /> Choose Image</Button>
              {form.image && <img src={form.image} alt="preview" className="h-16 rounded border" />}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setAdding(false); setForm({ name: "", image: "", width: 3.5, height: 2.2, unit: "inch", orientation: "landscape" }); }}>Cancel</Button>
            <Button onClick={saveTemplate} data-testid="template-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">Save Template</Button>
          </div>
        </div>
      )}

      {loading ? (
        <Loading text="Loading templates..." />
      ) : templates.length === 0 && !adding ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <LayoutTemplate className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-muted-foreground text-sm">No templates yet. Upload your ID/Certificate design with "Add Template".</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`template-card-${t.id.slice(0, 8)}`}>
              <div className="bg-slate-100 flex items-center justify-center p-3 h-36">
                <img src={t.image} alt={t.name} className="max-h-full max-w-full object-contain rounded shadow-sm" />
              </div>
              <div className="p-3">
                <p className="font-semibold text-sm truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.width} × {t.height} {t.unit} · {t.orientation} · {(t.layout || []).length} fields</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => setDesigning(t)} data-testid={`template-edit-btn-${t.id.slice(0, 8)}`} className="flex-1 gap-1 bg-cyan-600 hover:bg-cyan-700 text-white h-8">
                    <Pencil className="h-3.5 w-3.5" /> Edit / Design
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="h-8" data-testid={`template-settings-btn-${t.id.slice(0, 8)}`} title="Size / Image badlein">
                    <Settings2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(t.id, t.name)} className="text-red-500 hover:bg-red-50 h-8">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Template — {editing?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Template Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-template-name" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label>Width</Label>
                  <Input type="number" step="any" min="0.1" value={editForm.width} onChange={(e) => setEditForm({ ...editForm, width: parseFloat(e.target.value) || 0 })} data-testid="edit-template-width" />
                </div>
                <div className="space-y-1">
                  <Label>Height</Label>
                  <Input type="number" step="any" min="0.1" value={editForm.height} onChange={(e) => setEditForm({ ...editForm, height: parseFloat(e.target.value) || 0 })} data-testid="edit-template-height" />
                </div>
                <div className="space-y-1">
                  <Label>Unit</Label>
                  <Select value={editForm.unit} onValueChange={(v) => setEditForm({ ...editForm, unit: v })}>
                    <SelectTrigger data-testid="edit-template-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inch">inch</SelectItem>
                      <SelectItem value="cm">cm</SelectItem>
                      <SelectItem value="mm">mm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Orientation</Label>
                <Select value={editForm.orientation} onValueChange={(v) => setEditForm({ ...editForm, orientation: v })}>
                  <SelectTrigger data-testid="edit-template-orientation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landscape">Landscape</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Replace Template Image (optional)</Label>
                <input type="file" accept="image/*" ref={editFileRef} className="hidden" onChange={(e) => { onEditFile(e.target.files[0]); e.target.value = ""; }} />
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => editFileRef.current?.click()} className="gap-1.5" data-testid="edit-template-image-btn">
                    <Upload className="h-4 w-4" /> Choose New Image
                  </Button>
                  {editForm.image && <img src={editForm.image} alt="new template" className="h-14 rounded border" data-testid="edit-template-image-preview" />}
                </div>
                <p className="text-[11px] text-muted-foreground">Khali chhodne par purani image hi rahegi. Fields ki position proportionally scale hoti rahegi.</p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="edit-template-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
